import React, { useEffect, useState, useContext } from "react";
import { initSchedule } from "../../../utils/data/initData";
import { AuthContext } from "../../../contexts/AuthContext";
import { useErrorDialog } from "../../../contexts/ErrorDialogContext";
import { useSuccessDialog } from "../../../contexts/SuccessDialogContext";
import ComponentLoading from "../../../components/App/ComponentLoading";
import { handleScheduleDeletion } from "../utils/handleScheduleDeletion";
import InvestIncomeSchedule from "../../../components/Schedules/Others/InvestIncomeSchedule";
import { PL_SCHEDULES } from "../../../utils/data/controlData";
import { useAccessControl } from "../../../utils/hooks/useAccessControl";

const InvestmentIncomeSchedulePage = ({ company, scheduleId }) => {
  const { updatePLSchedule, getScheduleData, deleteScheduleByScheduleType } =
    useContext(AuthContext);

  const [saveTrigger, setSaveTrigger] = useState(false);
  const [schedule, setSchedule] = useState({});
  const [scheduleDetails, setScheduleDetails] = useState({});
  const [loading, setLoading] = useState(true);

  const { canEdit: canEditPLSchedule } = useAccessControl(PL_SCHEDULES);

  const handleError = useErrorDialog();
  const handleSuccess = useSuccessDialog();

  const saveSchedule = async (schedule, scheduleDetails) => {
    if (!company || !scheduleId) {
      handleError("Unexpected error happens. Please try again later.");
      return;
    }

    const updatedSchedule = {
      sch_id: scheduleId,
      scheduleType: "Other_Investment_Income",
      scheduleTitle: schedule.name,
      taxAdjustment: schedule.taxAdjustment,
      investIncomeRows: scheduleDetails.investIncomeRows,
      expensesRows: scheduleDetails.expensesRows,
      totalAdjustment: scheduleDetails.totalAdjustment,
      totalRevenue: scheduleDetails.totalRevenue,
      totalExpenses: scheduleDetails.totalExpenses,
      foreignTaxSuffered: scheduleDetails.foreignTaxSuffered,
      isTagToFTC: scheduleDetails.isTagToFTC,
      notes: schedule.notes,
      reviewNotes: schedule.reviewNotes,
      doc: scheduleDetails.doc
    };

    setLoading(true);
    const response = await updatePLSchedule(updatedSchedule);
    setLoading(false);

    if (response) {
      handleSuccess("The schedule has been saved successfully.");
      setSaveTrigger((prev) => !prev);
    } else {
      handleError(
        "There was a problem saving the schedule. Please contact support if the issue persists."
      );
    }
  };

  const deleteSchedule = async () => {
    await handleScheduleDeletion({
      scheduleId,
      deleteFn: deleteScheduleByScheduleType,
      handleSuccess,
      handleError,
      setLoading
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      if (!company || !scheduleId) {
        handleError(
          "We encountered an issue while fetching the data. Please refresh the page or try again shortly."
        );
        setLoading(false);
        return;
      }

      const response = await getScheduleData(
        scheduleId,
        "Other_Investment_Income"
      );
      const scheduleData = response?.schedule;

      // init
      if (scheduleData) {
        const scheduleTitle = "Other Income";
        setSchedule(initSchedule(scheduleData, scheduleTitle));

        setScheduleDetails({
          investIncomeRows: scheduleData.investIncomeRows || [],
          expensesRows: scheduleData.expensesRows || [],
          shareOfExpenses: scheduleData.shareOfExpenses || {
            amount: 0,
            source: ""
          },
          schRef: scheduleData.schRef || null,
          isTagToFTC: scheduleData.isTagToFTC ?? false,
          foreignTaxCreditScheduleRef:
            scheduleData.foreignTaxCreditScheduleRef || null,
          foreignTaxSuffered: scheduleData.foreignTaxSuffered ?? 0,
          doc: scheduleData.doc
        });
      }

      setLoading(false);
    };

    fetchData();
  }, [saveTrigger, scheduleId, getScheduleData, company?.uen]);

  return (
    <>
      {loading ? (
        <ComponentLoading />
      ) : (
        <InvestIncomeSchedule
          company={company}
          schedule={schedule}
          scheduleDetails={scheduleDetails}
          saveSchedule={saveSchedule}
          deleteSchedule={deleteSchedule}
          canEditSchedule={canEditPLSchedule}
        />
      )}
    </>
  );
};

export default InvestmentIncomeSchedulePage;
